import { useCallback, useEffect, useMemo, useRef, useState, type JSX } from 'react';
import frontPhotoPlaceholder from '../assets/photo-placeholders/front.webp';
import fullPhotoPlaceholder from '../assets/photo-placeholders/full.webp';
import profilePhotoPlaceholder from '../assets/photo-placeholders/profile.webp';
import type {
  CharacterPhotoDto,
  CharacterPhotoSlot,
  PhotoDto,
  PhotoReviewDto,
} from '../api/library.js';
import { PHOTO_ACCEPT, type UploadKind } from '../api/uploads.js';
import { useI18n } from '../i18n/context.js';
import { PhotoConsent } from './PhotoConsent.js';
import { usePhotoUpload } from './usePhotoUpload.js';

const CHARACTER_SLOTS = [
  'front',
  'profile',
  'full',
] as const satisfies readonly CharacterPhotoSlot[];
const CHARACTER_PHOTO_PLACEHOLDERS: Record<(typeof CHARACTER_SLOTS)[number], string> = {
  front: frontPhotoPlaceholder,
  profile: profilePhotoPlaceholder,
  full: fullPhotoPlaceholder,
};

/** Подсказки по съёмке из «Анкеты клиента» (01-SPEC.md §5.5). */
export function ShootingChecklist(): JSX.Element {
  const { t } = useI18n();
  const items = t.library.photos.checklist;
  return (
    <div>
      <h4 className="text-sm font-semibold">{t.library.photos.checklistTitle}</h4>
      <ul className="mt-2 grid list-disc gap-1 pl-5 text-sm text-[var(--kitup-color-muted)]">
        {(['daylight', 'noFilters', 'eyesOpen', 'plainBackground', 'sharp'] as const).map((key) => (
          <li key={key}>{items[key]}</li>
        ))}
      </ul>
    </div>
  );
}

function UploadIssues({ issues }: { issues: readonly string[] }): JSX.Element | null {
  const { t } = useI18n();
  if (issues.length === 0) return null;
  const messages = t.library.photos.upload;
  return (
    <ul role="status" className="mt-2 grid gap-1 text-sm text-amber-800">
      {issues.map((issue) => (
        <li key={issue}>{messages[issue as keyof typeof messages] ?? messages.failed}</li>
      ))}
    </ul>
  );
}

/**
 * Файлы можно отправлять в S3 параллельно, но итоговый список сохраняем по
 * очереди. Иначе два завершившихся PUT могут послать на API два списка,
 * собранных из одних и тех же устаревших props, и одна фотография потеряется.
 */
function useSerializedPhotoChanges<T>(
  photos: readonly T[],
  onChange: (photos: readonly T[]) => void | Promise<void>,
): (update: (photos: readonly T[]) => readonly T[]) => Promise<void> {
  const currentRef = useRef(photos);
  const onChangeRef = useRef(onChange);
  const queueRef = useRef<Promise<void>>(Promise.resolve());

  useEffect(() => {
    currentRef.current = photos;
  }, [photos]);
  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  return useCallback((update) => {
    const next = queueRef.current.then(async () => {
      const photos = update(currentRef.current);
      currentRef.current = photos;
      await onChangeRef.current(photos);
    });
    queueRef.current = next.catch(() => undefined);
    return next;
  }, []);
}

/** Три слота из анкеты: анфас, вполоборота, в полный рост. */
export function CharacterPhotoSlots({
  photos,
  issues = [],
  disabled,
  readOnly = false,
  onChange,
}: {
  photos: readonly CharacterPhotoDto[];
  /**
   * The server's review of the photos set (duplicates, low resolution), shown under the slot
   * it is about as soon as the photo is in — not only after three drawings missed.
   */
  issues?: PhotoReviewDto['issues'];
  disabled: boolean;
  readOnly?: boolean;
  onChange(
    photos: readonly { assetId: string; slot: CharacterPhotoSlot; order: number }[],
  ): void | Promise<void>;
}): JSX.Element {
  const { t } = useI18n();
  const upload = usePhotoUpload('photo');
  const current = useMemo(
    () =>
      photos.map((photo) => ({
        assetId: photo.assetId,
        slot: photo.slot,
        order: photo.order,
      })),
    [photos],
  );
  const changePhotos = useSerializedPhotoChanges(current, onChange);

  const setSlot = (slot: (typeof CHARACTER_SLOTS)[number], assetId: string | null): void => {
    void changePhotos((photos) => {
      const rest = photos.filter((photo) => photo.slot !== slot);
      return assetId ? [...rest, { assetId, slot, order: CHARACTER_SLOTS.indexOf(slot) }] : rest;
    });
  };

  return (
    <div className="grid gap-4">
      {readOnly ? null : <PhotoConsent />}
      <p className="text-sm text-[var(--kitup-color-muted)]">{t.library.photos.hintCharacter}</p>
      {/* Advice is for choosing photos, so it comes before the slots rather than after them. */}
      {readOnly ? null : <ShootingChecklist />}
      {/*
        Three across on a phone too: stacked, each slot was a screen-tall example figure, and
        the card ran to four screens. The figures are examples; small, they still say which shot.
      */}
      <div className="grid grid-cols-3 gap-2 sm:gap-3">
        {CHARACTER_SLOTS.map((slot) => {
          const photo = photos.find((item) => item.slot === slot);
          return (
            <div
              key={slot}
              className="kitup-card grid grid-rows-[auto_auto_auto] content-start gap-2 p-2 sm:p-3"
            >
              {/* Two lines kept for every label, so a long one doesn't push its picture below the others. */}
              <span className="min-h-[2lh] text-xs leading-tight font-medium sm:min-h-0 sm:text-sm">
                {t.library.photos.slots[slot]}
              </span>
              {/*
                `contain`, а не `cover`: по обрезанному кадру не понять, попал
                ли в него весь рост и не срезана ли макушка, — а именно это
                решает, годится ли фотография в референс.
              */}
              {photo ? (
                <img
                  src={photo.url}
                  alt={t.library.photos.slots[slot]}
                  className="aspect-square w-full rounded-[var(--kitup-radius-sm)] border border-transparent bg-[var(--kitup-color-accent)]/5 object-contain"
                />
              ) : (
                <img
                  src={CHARACTER_PHOTO_PLACEHOLDERS[slot]}
                  alt=""
                  aria-hidden="true"
                  className="aspect-square w-full rounded-[var(--kitup-radius-sm)] border border-dashed border-[var(--kitup-color-muted)]/30 bg-[var(--kitup-color-accent)]/5 object-contain opacity-75"
                />
              )}
              {/*
                One row of actions of the same height in every slot, filled or not: the native
                "Choose File · No file chosen" said "no file" under a photo, and a Remove button
                only on filled slots made the cards uneven.
              */}
              {readOnly ? null : (
                <div className="flex flex-wrap items-center gap-1 sm:gap-2">
                  <label
                    className={`kitup-secondary-button min-h-9 flex-1 cursor-pointer justify-center px-2 py-1.5 text-xs sm:px-3 sm:text-sm ${disabled ? 'pointer-events-none opacity-50' : ''}`}
                  >
                    <input
                      type="file"
                      accept={PHOTO_ACCEPT}
                      aria-label={t.library.photos.slots[slot]}
                      disabled={disabled}
                      className="sr-only"
                      onChange={(event) => {
                        const file = event.target.files?.[0];
                        if (!file) return;
                        void upload.upload(file).then((result) => {
                          if (result) setSlot(slot, result.assetId);
                        });
                        event.target.value = '';
                      }}
                    />
                    <span aria-hidden="true">
                      {photo ? t.library.photos.replace : t.library.photos.uploadPhoto}
                    </span>
                  </label>
                  {photo ? (
                    <button
                      type="button"
                      className="kitup-quiet-button min-h-9 flex-1 justify-center px-2 py-1.5 text-xs sm:flex-none sm:px-3 sm:text-sm"
                      onClick={() => setSlot(slot, null)}
                    >
                      {t.library.photos.remove}
                    </button>
                  ) : null}
                </div>
              )}
              {photo
                ? issues
                    // The review is of a particular file: a stale one about a photo since
                    // replaced in this slot is not said against the new one.
                    .filter(
                      (issue) =>
                        issue.slot === slot &&
                        issue.code !== 'slot_missing' &&
                        (issue.assetId === null || issue.assetId === photo.assetId),
                    )
                    .map((issue) => (
                      <p key={issue.code} role="status" className="text-sm text-amber-800">
                        {t.library.photos.issues[issue.code]}
                      </p>
                    ))
                : null}
            </div>
          );
        })}
      </div>
      {!readOnly && upload.uploading ? (
        <p role="status" className="text-sm">
          {t.library.photos.uploading}
        </p>
      ) : null}
      {readOnly ? null : <UploadIssues issues={upload.issues} />}
    </div>
  );
}

/** Простой список фото для вещей и локаций: слотов нет, порядок — по добавлению. */
export function PhotoList({
  photos,
  max,
  kind,
  hint,
  disabled,
  readOnly = false,
  onChange,
}: {
  photos: readonly PhotoDto[];
  max: number;
  kind: UploadKind;
  hint: string;
  disabled: boolean;
  readOnly?: boolean;
  onChange(photos: readonly { assetId: string; order: number }[]): void | Promise<void>;
}): JSX.Element {
  const { t } = useI18n();
  const upload = usePhotoUpload(kind);
  const current = useMemo(
    () => photos.map((photo, index) => ({ assetId: photo.assetId, order: index })),
    [photos],
  );
  const changePhotos = useSerializedPhotoChanges(current, onChange);
  const queuedCountRef = useRef(0);
  const [queuedCount, setQueuedCount] = useState(0);

  const reserveUploads = (count: number): void => {
    queuedCountRef.current += count;
    setQueuedCount(queuedCountRef.current);
  };

  const releaseUpload = (): void => {
    queuedCountRef.current = Math.max(0, queuedCountRef.current - 1);
    setQueuedCount(queuedCountRef.current);
  };

  return (
    <div className="grid gap-3">
      {readOnly ? null : <PhotoConsent />}
      <p className="text-sm text-[var(--kitup-color-muted)]">{hint}</p>
      <div className="flex flex-wrap gap-3">
        {photos.map((photo) => (
          <figure key={photo.assetId} className="grid w-28 gap-1">
            <img
              src={photo.url}
              alt=""
              className="aspect-square w-28 rounded-[var(--kitup-radius-sm)] bg-[var(--kitup-color-accent)]/5 object-contain"
            />
            {readOnly ? null : (
              <button
                type="button"
                className="kitup-quiet-button text-xs"
                onClick={() =>
                  void changePhotos((photos) =>
                    photos
                      .filter((item) => item.assetId !== photo.assetId)
                      .map((item, index) => ({ ...item, order: index })),
                  )
                }
              >
                {t.library.photos.remove}
              </button>
            )}
          </figure>
        ))}
      </div>
      {!readOnly && photos.length < max ? (
        <label
          className={`kitup-secondary-button min-h-9 w-fit cursor-pointer px-3 py-1.5 text-sm ${
            disabled || photos.length + queuedCount >= max ? 'pointer-events-none opacity-50' : ''
          }`}
        >
          <input
            type="file"
            accept={PHOTO_ACCEPT}
            multiple
            aria-label={t.library.photos.add}
            disabled={disabled || photos.length + queuedCount >= max}
            className="sr-only"
            onChange={(event) => {
              const available = Math.max(0, max - photos.length - queuedCountRef.current);
              const files = Array.from(event.target.files ?? []).slice(0, available);
              if (files.length === 0) return;
              reserveUploads(files.length);
              for (const file of files) {
                void upload
                  .upload(file)
                  .then((result) => {
                    if (!result) return;
                    return changePhotos((photos) => [
                      ...photos,
                      { assetId: result.assetId, order: photos.length },
                    ]);
                  })
                  .finally(releaseUpload);
              }
              event.target.value = '';
            }}
          />
          <span aria-hidden="true">+ {t.library.photos.addPhotos}</span>
        </label>
      ) : null}
      {!readOnly && (upload.uploading || queuedCount > 0) ? (
        <p role="status" className="text-sm">
          {t.library.photos.uploading}
        </p>
      ) : null}
      {readOnly ? null : <UploadIssues issues={upload.issues} />}
    </div>
  );
}
